import { Box, Text, Flex } from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  PortfolioChartConfig,
  portfolioFormatterMap,
} from './portfolioChartUtils';
import { PortfolioGridItem } from './PortfolioGridItem';
import { Typography } from 'packages/common-frontend/components/Typography';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';

// Fragments
type BooleanIconGridItemProps = {
  label: string;
  value: boolean;
  isActive?: boolean;
  cursor?: string;
  defaultTrueDisplayValue?: string;
  defaultFalseDisplayValue?: string;
};
const BooleanIconGridItem = ({
  label,
  value,
  isActive = false,
  cursor = 'default',
  defaultTrueDisplayValue = 'True',
  defaultFalseDisplayValue = 'False',
}: BooleanIconGridItemProps) => {
  return (
    <Box
      padding="16px 24px"
      borderWidth="1px"
      borderColor={isActive ? colors.black[70] : colors.gray[200]}
      borderRadius="8px"
      cursor={cursor}
    >
      <Text fontSize="xs" marginBottom="8px" color={colors.gray[500]}>
        {label}
      </Text>
      <Flex alignItems="center">
        {value === true ? (
          <Icon icon="circle-check" color={colors.success} size="large" />
        ) : (
          <Icon icon="circle-x" color={colors.error} size="large" />
        )}
        <Text fontWeight="bold" color={colors.black[95]} ml="16px">
          {value === true ? defaultTrueDisplayValue : defaultFalseDisplayValue}
        </Text>
      </Flex>
    </Box>
  );
};

// Exports
type BarData = {
  reportingDate: Util.FullDateStr;
  [key: string]: Util.Primitive;
};
type PortfolioChartCardsProps<T extends BarData> = {
  selectedBarData: T;
  chartConfig: PortfolioChartConfig<any>;
  activeFields: (keyof T)[];
  onToggleActiveField?: (selectedField: keyof T) => void;
  customTitleFormatter?: (selectedBarData: T) => string;
};
export function PortfolioChartCards<T extends BarData>({
  selectedBarData,
  chartConfig,
  activeFields,
  onToggleActiveField,
  customTitleFormatter,
}: PortfolioChartCardsProps<T>) {
  return (
    <Flex direction="column" gap="1rem" width="100%">
      <Typography fontWeight="semibold">
        {customTitleFormatter
          ? customTitleFormatter(selectedBarData)
          : `Reporting Date: ${commonDate.fullDateStrToHumanDateString(
              selectedBarData.reportingDate,
            )}`}
      </Typography>

      <Flex direction="column" gap="1rem" maxHeight="550px" overflowY="auto">
        {Object.entries(chartConfig).map(([fieldName, config]) => {
          if (!config) {
            return null;
          }
          const value = selectedBarData[fieldName];
          const { label, format, labelFormat } = config;
          const effectiveFormat = labelFormat || format;
          const formattedValue = portfolioFormatterMap[effectiveFormat](value);
          const isActive = activeFields.includes(fieldName);

          return (
            <Box
              key={fieldName}
              onClick={
                onToggleActiveField
                  ? () => onToggleActiveField(fieldName)
                  : undefined
              }
            >
              {effectiveFormat === 'boolean' && typeof value === 'boolean' ? (
                <BooleanIconGridItem
                  label={label}
                  value={value}
                  isActive={isActive}
                  cursor={onToggleActiveField ? 'pointer' : 'default'}
                  defaultTrueDisplayValue="Passed"
                  defaultFalseDisplayValue="Breached"
                />
              ) : (
                <PortfolioGridItem
                  label={label}
                  value={formattedValue}
                  isActive={isActive}
                  cursor={onToggleActiveField ? 'pointer' : 'default'}
                />
              )}
            </Box>
          );
        })}
      </Flex>
    </Flex>
  );
}
